import { useEffect } from "react";

/**
 * Direct positioning fix for Radix UI in WebView environments
 *
 * This approach directly positions tooltips and popovers relative to their triggers
 * without relying on Floating UI's broken calculations.
 */
export function PatchRadixForWebView() {
  useEffect(() => {
    // Only apply fix when body has position: fixed (WebView environment)
    if (document.body.style.position !== "fixed") {
      console.log(
        "PatchRadixForWebView: Body position is not fixed, skipping fix"
      );
      return;
    }

    const fixPositioning = () => {
      // Find all Radix UI content elements
      const allContent = document.querySelectorAll(
        "[data-radix-tooltip-content], [data-radix-popover-content]"
      );

      allContent.forEach((content) => {
        const wrapper = content.closest(
          "[data-radix-popper-content-wrapper]"
        ) as HTMLElement;
        if (!wrapper) return;

        // Check if positioning is broken (at 0,0)
        const rect = wrapper.getBoundingClientRect();
        if (rect.left === 0 && rect.top === 0) {
          // Find the trigger element
          let trigger: Element | null = null;

          // Try to find trigger by aria-describedby
          const contentId = content.getAttribute("id");
          if (contentId) {
            trigger = document.querySelector(
              `[aria-describedby="${contentId}"]`
            );
          }

          // Fallback: find by data-state
          if (!trigger) {
            trigger = document.querySelector('[data-state="open"]');
          }

          // Fallback: find by aria-expanded
          if (!trigger) {
            trigger = document.querySelector('[aria-expanded="true"]');
          }

          if (trigger && trigger instanceof HTMLElement) {
            const triggerRect = trigger.getBoundingClientRect();

            // Position the wrapper directly
            wrapper.style.position = "fixed";
            wrapper.style.left = `${triggerRect.left + triggerRect.width / 2}px`;
            wrapper.style.top = `${triggerRect.bottom + 8}px`;
            wrapper.style.transform = "translateX(-50%)";
            wrapper.style.zIndex = "10000";
          }
        }
      });
    };

    // Run immediately
    fixPositioning();

    // Set up observer for DOM changes
    const observer = new MutationObserver(() => {
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(fixPositioning);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state", "aria-describedby", "aria-expanded"]
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return null;
}
